import type { PeriodModel } from '@frontend/modules/order/components/filter/order-filter-input.interface';

/** The Orders page presets, plus no limit at all. */
export type StatsFilterPeriod = PeriodModel | 'ALL';

export const allowedStatsFilterPeriods: StatsFilterPeriod[] = [
  'ALL',
  'TODAY',
  'THIS_WEEK',
  'THIS_MONTH',
];

export interface StatsFilterInput {
  /** `['ALL']` in the form represents "all places"; `undefined` after submit means "all places". */
  placeIds: string[];
  period: StatsFilterPeriod;
}

export interface StatsFilter {
  placeIds: string[] | undefined;
  period: StatsFilterPeriod;
}

export const getDefaultStatsFilter = (): StatsFilter => ({
  placeIds: undefined,
  period: 'TODAY',
});

/** Reads a filter from storage, falling back to the defaults for anything that is not valid. */
export const toValidStatsFilter = (cached: unknown): StatsFilter => {
  const defaults = getDefaultStatsFilter();
  const { period, placeIds } = (cached ?? {}) as Record<string, unknown>;

  return {
    period: allowedStatsFilterPeriods.includes(period as StatsFilterPeriod)
      ? (period as StatsFilterPeriod)
      : defaults.period,
    placeIds:
      Array.isArray(placeIds) &&
      placeIds.length > 0 &&
      placeIds.every((id) => typeof id === 'string')
        ? placeIds
        : defaults.placeIds,
  };
};

/**
 * Reads the filter from the persisted stats page settings. A value that is not
 * valid JSON, or not an object, gives the defaults instead of throwing on start.
 */
export const parseStoredStatsFilter = (stored: string): StatsFilter => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(stored);
  } catch {
    parsed = undefined;
  }

  return toValidStatsFilter(
    parsed && typeof parsed === 'object'
      ? (parsed as { filter?: unknown }).filter
      : undefined,
  );
};
